import React from 'react';
import PublicHeader, { LOGIN_HREF, startLogin } from '../PublicHeader';
import PublicFooter from '../PublicFooter';

const content = {
    bs: {
        nav: {
            home: 'Početna',
            mobile: 'Mobilna aplikacija',
            privacy: 'Privatnost',
            login: 'Prijava'
        },
        badge: 'Informacioni sistem · UNSA',
        sub: 'Jedinstveni informacioni sistem Univerziteta u Sarajevu',
        desc: 'Jedan sistem za studente, nastavno osoblje i službe Univerziteta. Od upisa i organizacije nastave, preko ispita i ocjena, do izdavanja uvjerenja i diploma — eUNSA objedinjuje evidencije svih organizacionih jedinica na jednom mjestu.',
        loginBtn: 'Prijava na sistem',
        mobileBtn: 'Mobilna aplikacija za studente',
        mobileNote: 'Mobilna aplikacija namijenjena je isključivo studentima. Nastavno osoblje i službe Univerziteta sistemu pristupaju putem web aplikacije.',
        powered: 'Powered by',
        secTitle: 'Šta eUNSA omogućava',
        secSub: 'Jedna evidencija, različiti pogledi — svako vidi ono što mu treba za rad',
        features: [
            { t: 'Studenti', d: 'Uvid u semestre, predmete i ocjene, prijava ispita, studentske ankete i zahtjevi za uvjerenja. Jedina uloga kojoj je dostupna i mobilna aplikacija.' },
            { t: 'Nastavno osoblje', d: 'Evidencija nastave i ispita, unos i ovjera ocjena, pregled angažmana, mentorstva i komisije.' },
            { t: 'Studentska služba', d: 'Upis i statusi studenata, semestri, finansiranje studija te izdavanje uvjerenja i diploma iz jedinstvene evidencije.' },
            { t: 'Kadrovska služba', d: 'Evidencija zaposlenika i angažovanog osoblja, ugovori, akademska zvanja i sistematizacija radnih mjesta.' },
            { t: 'Međunarodna saradnja', d: 'Dolazna i odlazna mobilnost (Erasmus+, CEEPUS), registracija eksternih studenata i priznavanje ispita.' },
            { t: 'Izvještavanje', d: 'Statistika i izvještaji za organizacione jedinice i nadležne institucije, uz kontrolu pristupa po ulogama.' }
        ],
    },
    en: {
        nav: {
            home: 'Home',
            mobile: 'Mobile app',
            privacy: 'Privacy',
            login: 'Sign in'
        },
        badge: 'Information system · UNSA',
        sub: 'The Unified Information System of the University of Sarajevo',
        desc: 'One system for students, teaching staff and the administrative services of the University. From enrollment and the organization of teaching, through exams and grades, to issuing certificates and diplomas — eUNSA brings the records of all organizational units together in one place.',
        loginBtn: 'Sign in to the system',
        mobileBtn: 'Mobile app for students',
        mobileNote: 'The mobile application is intended for students only. Teaching staff and University services access the system through the web application.',
        powered: 'Powered by',
        secTitle: 'What eUNSA provides',
        secSub: 'One set of records, different views — everyone sees what they need for their work',
        features: [
            { t: 'Students', d: 'Access to semesters, courses and grades, exam registration, student surveys and certificate requests. The only role that also has a mobile app.' },
            { t: 'Teaching staff', d: 'Records of teaching and exams, entry and confirmation of grades, overview of engagements, mentorships and committees.' },
            { t: 'Student services', d: 'Enrollment and student statuses, semesters, study financing, and issuing certificates and diplomas from unified records.' },
            { t: 'Human resources', d: 'Records of employees and engaged staff, contracts, academic titles and job classification.' },
            { t: 'International cooperation', d: 'Incoming and outgoing mobility (Erasmus+, CEEPUS), registration of external students and recognition of exams.' },
            { t: 'Reporting', d: 'Statistics and reports for organizational units and competent institutions, with role-based access control.' }
        ],
    }
};

function getInitialLang() {
    try { return localStorage.getItem('publicLang') || 'bs'; } catch (e) { return 'bs'; }
}

export default function Landing() {
    const [lang, setLang] = React.useState(getInitialLang);
    const c = content[lang];

    function changeLang(l) {
        try { localStorage.setItem('publicLang', l); } catch (e) { /* ignore */ }
        setLang(l);
    }

    const navLinks = [
        { href: '#pocetna', label: c.nav.home },
        { href: '/public/mobile', label: c.nav.mobile },
        { href: '/public/privacy-policy', label: c.nav.privacy }
    ];

    return (
        <React.Fragment>
            <style>{`
                .web-landing {
                    --bg: #080c14;
                    --navy: #141d2b;
                    --blue: #1d6ea4;
                    --blue-light: #5aa1d8;
                    --text: #eaf1f8;
                    --muted: #8a97a9;
                    --card: rgba(255, 255, 255, 0.04);
                    --border: rgba(255, 255, 255, 0.08);
                    --radius: 18px;
                    font-family: 'Inter', system-ui, sans-serif;
                    color: var(--text);
                    background: var(--bg);
                    line-height: 1.6;
                    overflow-x: hidden;
                    min-height: 100vh;
                    position: relative;
                    scroll-behavior: smooth;
                    -webkit-font-smoothing: auto;
                    -moz-osx-font-smoothing: auto;
                }
                .web-landing * { margin: 0; padding: 0; box-sizing: border-box; font-family: 'Inter', system-ui, sans-serif; }
                .web-landing::before {
                    content: ""; position: fixed; inset: 0; z-index: 0;
                    background:
                        radial-gradient(circle at 50% 35%, rgba(29, 110, 164, 0.28), transparent 55%),
                        radial-gradient(circle at 85% 80%, rgba(29, 110, 164, 0.18), transparent 50%),
                        var(--bg);
                }
                .web-landing::after {
                    content: ""; position: fixed; inset: 0; z-index: 0;
                    background-image: radial-gradient(rgba(255,255,255,0.05) 1px, transparent 1px);
                    background-size: 26px 26px;
                    mask-image: radial-gradient(circle at 50% 40%, #000 30%, transparent 85%);
                    -webkit-mask-image: radial-gradient(circle at 50% 40%, #000 30%, transparent 85%);
                }
                .web-landing main, .web-landing footer { position: relative; z-index: 1; }
                .web-landing .wrap { max-width: 1160px; margin: 0 auto; padding: 0 24px; }

                /* HERO */
                .web-landing .hero { padding: 90px 0 60px; max-width: 820px; }
                .web-landing .badge {
                    display: inline-flex; align-items: center; gap: 8px;
                    background: var(--card); border: 1px solid var(--border);
                    padding: 7px 14px; border-radius: 100px; font-size: 13px; color: var(--blue-light);
                    font-weight: 600; margin-bottom: 24px;
                }
                .web-landing .badge .dot { width: 7px; height: 7px; border-radius: 50%; background: var(--blue-light); box-shadow: 0 0 10px var(--blue-light); }
                .web-landing h1 { font-size: clamp(52px, 8vw, 86px); line-height: 1; font-weight: 900; letter-spacing: -2.5px; color: #fff; }
                .web-landing h1 .brand-e { color: var(--blue-light); }
                .web-landing .sub { color: var(--text); font-size: 22px; margin: 20px 0 14px; font-weight: 600; }
                .web-landing .desc { color: var(--muted); font-size: 16px; margin-bottom: 36px; }

                .web-landing .cta-row { display: flex; gap: 14px; flex-wrap: wrap; align-items: center; }
                .web-landing .btn {
                    display: inline-flex; align-items: center; gap: 10px; text-decoration: none;
                    padding: 13px 26px; border-radius: 14px; font-size: 16px; font-weight: 700;
                    transition: transform .15s, box-shadow .2s, border-color .2s, background .2s;
                }
                .web-landing .btn svg { width: 19px; height: 19px; }
                .web-landing .btn-primary {
                    background: linear-gradient(120deg, var(--blue), var(--blue-light));
                    color: #fff; box-shadow: 0 10px 30px rgba(29,110,164,.35);
                }
                .web-landing .btn-primary:hover { transform: translateY(-3px); box-shadow: 0 16px 40px rgba(90,161,216,.4); }
                .web-landing .btn-ghost { background: var(--card); color: var(--text); border: 1px solid var(--border); }
                .web-landing .btn-ghost:hover { border-color: rgba(90,161,216,.45); background: rgba(29,110,164,.1); }

                .web-landing .mobile-note {
                    margin-top: 18px; color: var(--muted); font-size: 14px; max-width: 620px;
                    border-left: 2px solid rgba(90,161,216,.5); padding-left: 14px;
                }

                .web-landing .powered { margin-top: 26px; font-size: 13px; color: var(--muted); letter-spacing: 1px; }
                .web-landing .powered b { color: var(--blue-light); font-weight: 600; }

                /* FEATURES */
                .web-landing .features { padding: 60px 0 20px; }
                .web-landing .sec-title { text-align: center; font-size: 32px; font-weight: 800; letter-spacing: -.5px; margin-bottom: 10px; }
                .web-landing .sec-sub { text-align: center; color: var(--muted); margin-bottom: 46px; }
                .web-landing .grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 18px; }
                @media (max-width: 860px) { .web-landing .grid { grid-template-columns: 1fr; } }
                .web-landing .card {
                    background: var(--card); border: 1px solid var(--border); border-radius: var(--radius);
                    padding: 26px; transition: transform .2s, border-color .2s, background .2s;
                }
                .web-landing .card:hover { transform: translateY(-4px); border-color: rgba(90,161,216,.4); background: rgba(29,110,164,.08); }
                .web-landing .ic {
                    width: 48px; height: 48px; border-radius: 12px; display: grid; place-items: center;
                    background: rgba(29,110,164,.18); color: var(--blue-light); margin-bottom: 16px;
                }
                .web-landing .ic svg { width: 24px; height: 24px; }
                .web-landing .card h3 { font-size: 18px; font-weight: 700; margin-bottom: 6px; }
                .web-landing .card p { color: var(--muted); font-size: 15px; }

                .web-landing footer { position: relative; z-index: 1; }
            `}</style>

            <div className="web-landing">
                <PublicHeader
                    lang={lang}
                    onLangChange={changeLang}
                    links={navLinks}
                    loginLabel={c.nav.login}
                />

                <main className="wrap">

                    {/* HERO */}
                    <section className="hero" id="pocetna">
                        <span className="badge"><span className="dot" /> {c.badge}</span>
                        <h1><span className="brand-e">e</span>UNSA</h1>
                        <p className="sub">{c.sub}</p>
                        <p className="desc">{c.desc}</p>

                        <div className="cta-row">
                            <a className="btn btn-primary" href={LOGIN_HREF} onClick={startLogin}>
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4" />
                                    <path d="M10 17l5-5-5-5M15 12H3" />
                                </svg>
                                {c.loginBtn}
                            </a>
                            <a className="btn btn-ghost" href="/public/mobile">
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <rect x="7" y="2" width="10" height="20" rx="2" />
                                    <path d="M11 18h2" />
                                </svg>
                                {c.mobileBtn}
                            </a>
                        </div>

                        <p className="mobile-note">{c.mobileNote}</p>

                        <p className="powered">{c.powered} <b>UTIC</b> · Univerzitet u Sarajevu</p>
                    </section>

                    {/* MOGUCNOSTI */}
                    <section className="features" id="mogucnosti">
                        <h2 className="sec-title">{c.secTitle}</h2>
                        <p className="sec-sub">{c.secSub}</p>
                        <div className="grid">
                            <div className="card">
                                <div className="ic"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 2 3 7l9 5 9-5-9-5Z" /><path d="M3 7v6l9 5 9-5V7" /></svg></div>
                                <h3>{c.features[0].t}</h3>
                                <p>{c.features[0].d}</p>
                            </div>
                            <div className="card">
                                <div className="ic"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" /><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2Z" /></svg></div>
                                <h3>{c.features[1].t}</h3>
                                <p>{c.features[1].d}</p>
                            </div>
                            <div className="card">
                                <div className="ic"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8Z" /><path d="M14 2v6h6M9 15l2 2 4-4" /></svg></div>
                                <h3>{c.features[2].t}</h3>
                                <p>{c.features[2].d}</p>
                            </div>
                            <div className="card">
                                <div className="ic"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" /></svg></div>
                                <h3>{c.features[3].t}</h3>
                                <p>{c.features[3].d}</p>
                            </div>
                            <div className="card">
                                <div className="ic"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10" /><path d="M2 12h20" /><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10Z" /></svg></div>
                                <h3>{c.features[4].t}</h3>
                                <p>{c.features[4].d}</p>
                            </div>
                            <div className="card">
                                <div className="ic"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 3v18h18" /><path d="M7 15l4-4 3 3 5-6" /></svg></div>
                                <h3>{c.features[5].t}</h3>
                                <p>{c.features[5].d}</p>
                            </div>
                        </div>
                    </section>

                </main>

                <PublicFooter lang={lang} />
            </div>
        </React.Fragment>
    );
}



// WEBPACK FOOTER //
// ./src/views/Public/Landing/Landing.js